import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { Text } from '@renderer-ui-library/atoms';
import { Typography } from '@renderer-ui-library/mui';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper';
import { BlockProps } from 'blocks/BlockProps';
import { translations } from 'i18n/translations';
import { useTranslation } from 'next-i18next';
import React, { useCallback } from 'react';
import { DetailsTable } from '../../renderer-ui-library/organisms/DetailsTable/DetailsTable';
import { IMachineDetailsBlockData } from './IMachineDetailsBlockData';
import styles from './machineDetailsBlock.module.scss';

export const MachineDetailsBlock: React.FC<
    BlockProps<IMachineDetailsBlockData>
> = React.memo(props => {
    const { t } = useTranslation(localeNamespace.common);
    const pdpMeta = props.pagePropsMetaData.pdp;

    const { isMobile } = useMediaQuery();

    const getUnderlinedTitleComponent = useCallback(
        (title: string) => (
            <div className={styles.underlinedTitle}>
                <Typography
                    variant="h4"
                    component="h3"
                    className={styles.underlinedTitleText}
                >
                    {title}
                </Typography>
            </div>
        ),
        [],
    );

    if (!pdpMeta || !pdpMeta.machine) {
        return null;
    }
    const machine = pdpMeta.machine;
    const basicDataDefinition =
        pdpMeta.displayDefinitions.WEBSITE_MACHINE_BASICDATA;
    const machineFeaturesDefinition =
        pdpMeta.displayDefinitions.WEBSITE_MACHINE_FEATURES;
    const furtherMachineConfigurationDisplayDefinition =
        pdpMeta.displayDefinitions.WEBSITE_FURTHER_MACHINE_CONFIGURATION;

    return (
        <BlockWrapper blockData={props.data} noSpacingTop>
            <div className={styles.wrapper}>
                <Typography
                    variant={isMobile ? 'h2' : 'h3'}
                    component="h2"
                    className={styles.title}
                >
                    {t(translations.machineDetailsTitle)}
                </Typography>

                {basicDataDefinition && (
                    <div className={styles.tableWrapper}>
                        <DetailsTable
                            displayDefinition={basicDataDefinition}
                            locale={props.locale}
                            machine={machine}
                            titleComponent={getUnderlinedTitleComponent(
                                t(
                                    translations.machineBasicDataDisplayDefinitionTitle,
                                ),
                            )}
                            showCategoriesTitle={false}
                        />
                    </div>
                )}

                {!!machineFeaturesDefinition?.categories.length && (
                    <div className={styles.tableWrapper}>
                        <DetailsTable
                            displayDefinition={machineFeaturesDefinition}
                            locale={props.locale}
                            machine={machine}
                            titleComponent={getUnderlinedTitleComponent(
                                t(
                                    translations.machineFeaturesDisplayDefinitionTitle,
                                ),
                            )}
                            showCategoriesTitle
                        />
                    </div>
                )}
                {!!furtherMachineConfigurationDisplayDefinition?.categories
                    ?.length && (
                    <div className={styles.tableWrapper}>
                        <DetailsTable
                            displayDefinition={
                                furtherMachineConfigurationDisplayDefinition
                            }
                            locale={props.locale}
                            machine={machine}
                            titleComponent={getUnderlinedTitleComponent(
                                t(
                                    translations.furtherMachineConfigurationDisplayDefinitionTitle,
                                ),
                            )}
                            showCategoriesTitle
                        />
                    </div>
                )}
                <div className={styles.disclaimer}>
                    <div className={styles.disclaimerMarker}>*</div>
                    <div>
                        {isMobile && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                marginBottom={2}
                            >
                                {t(translations.serviceFeeDisclaimer)}
                            </Typography>
                        )}
                        <Typography variant="body1" gutterBottom>
                            {t(translations.machineDetailsDisclaimerTitle)}
                        </Typography>
                        <Text
                            variant="body2"
                            text={t(translations.machineDetailsDisclaimer)}
                        />
                    </div>
                </div>
            </div>
        </BlockWrapper>
    );
});

MachineDetailsBlock.displayName = 'MachineDetailsBlock';
